<template>
  <div>
    <v-btn color="secondary" x-small @click="dialogOpen=true" :disabled="disableWhenClosed">
      <v-icon small class="mr-1">{{ mdiEmailOutline }}</v-icon>
      Send Invoice
    </v-btn>

    <v-dialog v-model="dialogOpen" persistent max-width="1200">
      <v-card :loading="getIsFetchProformaInvoiceDataLoading" :disabled="getIsFetchProformaInvoiceDataLoading">
        <v-card-title>Ad Hoc Invoice Summary</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-card>
                <v-card-title>Billing Contact</v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="d-flex flex-column">
                        <span class="text-caption">Customer Name</span>
                        <span class="font-weight-bold">{{ getCurrentAccount.first_name | capitalizeString }} {{ getCurrentAccount.last_name | capitalizeString }}</span>
                      </div>
                    </v-col>

                    <v-col cols="12" md="6">
                      <div class="d-flex flex-column">
                        <span class="text-caption">Business Name</span>
                        <span class="font-weight-bold">{{ getCurrentAccount.business_name | capitalizeString }}</span>
                      </div>
                    </v-col>

                    <v-col cols="12">
                      <div class="d-flex flex-column">
                        <span class="text-caption">Email address</span>
                        <span class="font-weight-bold">{{ getCurrentAccount.email_address }}</span>
                      </div>
                    </v-col>

                    <v-col cols="12" md="6">
                      <div class="d-flex flex-column">
                        <span class="text-caption">Mobile Number</span>
                        <span class="font-weight-bold">{{ getCurrentAccount.mobile_number }}</span>
                      </div>
                    </v-col>

                    <v-col cols="12" md="6">
                      <div class="d-flex flex-column">
                        <span class="text-caption">Address</span>
                        <span class="font-weight-bold">{{ getCurrentAccount.address_line_one | capitalizeString }}</span>
                        <span class="font-weight-bold">{{ getCurrentAccount.address_line_two | capitalizeString }}</span>
                        <span class="font-weight-bold">{{ getCurrentAccount.address_city | capitalizeString }}</span>
                        <span class="font-weight-bold">{{ getCurrentAccount.address_post_code | capitalizeString }}</span>
                        <span class="font-weight-bold">{{ getCurrentAccount.address_country | capitalizeString }}</span>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="5">
              <v-card height="100%">
                <v-card-title>Line Items</v-card-title>

                <v-card-text>
                  <div v-for="(item, i) in lineItems" :key="i">

                    <div class="d-flex justify-space-between line-items mb-2">
                      <v-tooltip top :disabled="!disableLineItem(item)">
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on">
                            <v-checkbox v-model="item.selected" hide-details :label="item.description" @change="resetQuantity(item)" :disabled="disableLineItem(item)"></v-checkbox>
                          </div>
                        </template>

                        <span>This is a mandatory fee for cancellation</span>
                      </v-tooltip>
                      
                      <div class="d-flex justify-space-between text-fields">
                        <v-text-field v-model="item.quantity" :disabled="!item.selected || disableLineItem(item)" outlined hide-details type="number" label="Quantity" class="mr-2" min="1"></v-text-field>
                        <v-text-field id="price-field" v-model="item.item_price" :disabled="!item.selected || disableLineItem(item)" outlined hide-details label="Price"></v-text-field>
                      </div>
                    </div>

                    <div v-if="$v.lineItems.$each[i].$invalid" class="text-caption error--text ml-2 mb-2 mt-n1">
                      {{ itemPriceErrors(i)[0] }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="3">
              <v-card height="100%">
                <v-card-title>Summary</v-card-title>
                <v-card-text>
                  <div class="d-flex justify-space-between mb-1">
                    <span class="font-weight-bold">Excl. VAT</span>
                    <span class="font-weight-bold">{{ getSummaryTotals.exvat | formatCurrency }}</span>
                  </div>

                  <div class="d-flex justify-space-between mb-1">
                    <span class="font-weight-bold">VAT</span>
                    <span class="font-weight-bold">{{ getSummaryTotals.vat | formatCurrency }}</span>
                  </div>

                  <div class="d-flex justify-space-between mb-1">
                    <span class="dark_grey--text font-weight-bold">Total</span>
                    <span class="font-weight-bold">{{ getSummaryTotals.total | formatCurrency }}</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" small :loading="updateLoading" @click="updateLineItems">SEND INVOICE</v-btn>
          <v-btn color="primary" small :disabled="updateLoading" @click="close">CANCEL</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { mdiEmailOutline } from '@mdi/js';
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";

export default {
  mixins: [ validationMixin ],

  validations: {
    lineItems: {
      $each: {
        item_price: { 
          required: requiredIf((vm) => vm.selected), 
          isDecimal: (value, vm) => {
            if(!vm.selected) return true;
            if(!value) return false;
            return /^[0-9]*\.[0-9]{2}$/.test(value);
          }
        },
        quantity: {
          required: requiredIf((vm) => vm.selected),
          isPositive: (value, vm) => { 
            if(!vm.selected) return true;
            return value > 0;
          }
        }
      }
    }
  },

  props: {
    accountId: {
      type: [String, Number],
      default: ""
    }
  },

  data() {
    return {
      dialogOpen: false,
      mdiEmailOutline,
      lineItems: [],
      updateLoading: false,
    }
  },

  computed: {
    ...mapGetters("accounts", ["getCurrentAccount"]),
    ...mapGetters('retention', ['getSingleRetentionLead']),
    ...mapGetters("retention/adhocInvoice", ["getAdhocInvoiceData", "getIsFetchAdhocInvoiceDataLoading", "getIsDownloadAdhocInvoiceDataLoading", "getIsUpdateAdhocInvoiceDataLoading", "getIsUpdateAdhocInvoiceDataError"]),
    ...mapGetters("retention/proformaInvoice", ["getProformaInvoiceData", "getIsFetchProformaInvoiceDataLoading", "getIsDownloadProformaInvoiceDataLoading", "getIsUpdateProformaInvoiceDataLoading", "getIsUpdateProformaInvoiceDataError"]),

    getSummaryTotals() {
      return this.lineItems.reduce((acc, obj) => {
        if(obj.selected) {
          acc.exvat += parseFloat(obj.item_price * obj.quantity);
          acc.vat = acc.exvat * parseFloat(this.$vatAmount);
          acc.total = acc.exvat * parseFloat(this.$vatAmount + 1);
        }
        return acc;
      }, { total: 0.00, exvat: 0.00, vat: 0.00 });
    },

    getLineItemsPayload() {
      return this.lineItems.reduce((acc, obj) => {
        if(obj.selected) {
          acc.line_items.push({
            product: obj.description,
            quantity: obj.quantity,
            total: (obj.quantity * obj.item_price).toFixed(2)
          });
        }
        return acc;
      }, { line_items: [] });
    },

    itemPriceErrors() {
      return (i) => {
        const errors = [];
        !this.$v.lineItems.$each[i].quantity.required && errors.push("Quantity is a required field");
        !this.$v.lineItems.$each[i].quantity.isPositive && errors.push("Quantity must be a number greater than 0");
        !this.$v.lineItems.$each[i].item_price.required && errors.push("Price is a required field");
        !this.$v.lineItems.$each[i].item_price.isDecimal && errors.push("Price must be a positive decimal number eg. 10.00");
        return errors;
      }
    },

    disableWhenClosed() {
      return this.getSingleRetentionLead.status === 'Closed';
    }
  },

  watch: {
    dialogOpen: {
      async handler(newVal) {
        if(newVal) {
          await this.fetchProformaInvoiceData(this.$route.params.id);
          this.setupLineItems();
        }
      }
    }
  },

  methods: {
    ...mapActions("retention", ["getRetentionLead"]),
    ...mapActions("retention/proformaInvoice", ["fetchProformaInvoiceData"]),
    ...mapActions("retention/adhocInvoice", ["updateAdhocInvoiceData"]),
    ...mapActions("accounts", { fetchCurrentAccount: "getCurrentAccount" }),

    setupLineItems() {
      this.lineItems = [];
      this.getProformaInvoiceData.line_items.forEach(item => {
        this.lineItems.push({
          ...item,
          quantity: item.quantity,
          item_price: (item.total / item.quantity).toFixed(2),
          selected: true,
        });
      });
    },

    async updateLineItems() {
      this.$v.$touch();
      if(!this.$v.$invalid) {
        this.updateLoading = true;
        await this.updateAdhocInvoiceData({ leadId: this.$route.params.id, payload: this.getLineItemsPayload });

        if(!this.getIsUpdateAdhocInvoiceDataError) {
          await new Promise(resolve => setTimeout(async () => {
            await this.getRetentionLead(this.$route.params.id);
            await this.fetchCurrentAccount(this.accountId);
            resolve();
            this.dialogOpen = false;
          }, 2000));
        }
        this.updateLoading = false;
      }
    },

    resetQuantity(item) {
      if(!item.quantity || item.quantity < 1) {
        item.quantity = 1;
      }
    },

    close() {
      this.$v.$reset();
      this.setupLineItems();
      this.dialogOpen = false;
    },

    disableLineItem(item) {
      return item.description === 'Cancellation Fee' || item.description === 'Disconnection Fee';
    }
  }
};
</script>

<style lang="scss" scoped>
.text-fields { 
  .v-text-field {
    max-width: 100px;
  }
}
</style>
